import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useRef } from 'react'

interface TableProps<TData> {
  data: TData[]
  columns: ColumnDef<TData, any>[]
  getRowId?: (row: TData) => string
  onRowClick?: (row: TData) => void
  className?: string
  selectedId?: string
  infiniteScrollRef?: React.RefObject<HTMLDivElement>
  isFetchingMore?: boolean
  manualSorting?: boolean
  onSortChange?: (columnId: string) => void
  getSortState?: (columnId: string) => 'asc' | 'desc' | false
}

export const Table = <TData,>({
  data,
  columns,
  getRowId,
  onRowClick,
  className,
  selectedId,
  infiniteScrollRef,
  isFetchingMore,
  manualSorting,
  onSortChange,
  getSortState,
}: TableProps<TData>) => {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId,
    manualSorting,
  })

  const tableContainerRef = useRef<HTMLDivElement>(null)
  const tbodyRef = useRef<HTMLTableSectionElement>(null)

  const { rows } = table.getRowModel()

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 37,
    overscan: 10,
  })

  const fullClassName =
    `table table-sm table-fixed border border-base-content/5 bg-base-100 ${className ?? ''}`.trim()

  const virtualItems = rowVirtualizer.getVirtualItems()
  const totalSize = rowVirtualizer.getTotalSize()

  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0
  const paddingBottom =
    virtualItems.length > 0
      ? totalSize - virtualItems[virtualItems.length - 1].end
      : 0

  return (
    <div ref={tableContainerRef} className="overflow-auto h-full">
      <table className={fullClassName}>
        <thead className="sticky top-0 bg-base-100">
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                const canSort =
                  header.column.columnDef.enableSorting &&
                  onSortChange &&
                  getSortState
                const sortState = canSort
                  ? getSortState(header.column.id)
                  : false

                return (
                  <th
                    key={header.id}
                    className={canSort ? 'cursor-pointer select-none' : ''}
                    onClick={() => canSort && onSortChange(header.column.id)}
                  >
                    {header.isPlaceholder ? null : (
                      <div className="flex items-center gap-1">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {canSort && (
                          <span className="text-xs">
                            {sortState === 'asc'
                              ? '↑'
                              : sortState === 'desc'
                                ? '↓'
                                : '⇅'}
                          </span>
                        )}
                      </div>
                    )}
                  </th>
                )
              })}
            </tr>
          ))}
        </thead>
        <tbody ref={tbodyRef}>
          {paddingTop > 0 && (
            <tr>
              <td style={{ height: `${paddingTop}px` }} />
            </tr>
          )}
          {virtualItems.map(virtualRow => {
            const row = rows[virtualRow.index]
            const rowData = row.original
            const isSelected = selectedId === row.id
            const selectionClassName = isSelected ? 'bg-primary/10' : ''

            return (
              <tr key={row.id} className={selectionClassName}>
                {row.getVisibleCells().map((cell, index) => (
                  <td key={cell.id}>
                    {index === 0 && onRowClick ? (
                      <button
                        type="button"
                        className="link link-info text-left"
                        onClick={() => onRowClick(rowData)}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </button>
                    ) : (
                      flexRender(cell.column.columnDef.cell, cell.getContext())
                    )}
                  </td>
                ))}
              </tr>
            )
          })}
          {paddingBottom > 0 && (
            <tr>
              <td style={{ height: `${paddingBottom}px` }} />
            </tr>
          )}
          {infiniteScrollRef && (
            <tr>
              <td>
                <div ref={infiniteScrollRef} className="h-4" />
              </td>
            </tr>
          )}
          {isFetchingMore && (
            <tr>
              <td colSpan={columns.length}>
                <div className="flex justify-center py-4">
                  <span className="loading loading-spinner loading-md" />
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}
